/**
 * @file Pure section management logic — zero DOM dependency.
 *
 * During Vue migration:
 *   - addSectionToPage    → usePagesStore().addSection()
 *   - removeSectionFromPage → usePagesStore().removeSection()
 *   - getProgressInfo     → usePagesStore().progress (computed)
 *   - getStatusText       → usePagesStore().statusText (computed)
 *   - canExport           → usePagesStore().canExport (computed)
 *
 * Note: createSection/createPage/reorderSection/getBuildStatus
 * are already in logic/pages.js — this file adds higher-level
 * orchestration that was embedded in screens/sections.js.
 */
import type { ConceptSection, Page, Section, SectionType, SectionTypeId } from '../types.ts';

// ── SECTION ADD/REMOVE (state mutations) ───────────────────────────

export interface AddSectionResult {
  section: Section;
  conceptSection: ConceptSection;
}

/**
 * Create a section + concept section pair from a type definition.
 * Pure factory — does not mutate any arrays.
 */
export const makeSectionPair = (
  typeId: SectionTypeId,
  sectionTypes: SectionType[],
  existingSects: Section[],
  idGenerator: (type: SectionTypeId, sects: Section[]) => string,
): AddSectionResult => {
  const def = sectionTypes.find(t => t.id === typeId) || {
    id: typeId,
    color: '#64748b',
    desc: '',
  };
  const title = def.id.charAt(0).toUpperCase() + def.id.slice(1).replace(/-/g, ' ');
  const id = idGenerator(typeId, existingSects);

  return {
    section: {
      id,
      type: def.id,
      title,
      purpose: def.desc,
      html: '',
      status: 'pending',
      note: '',
      error: '',
      replaceImg: null,
      screenshotImg: null,
      editLog: [],
    },
    conceptSection: {
      id,
      type: def.id,
      title,
      purpose: def.desc,
      visualBrief: '',
      layout: 'auto',
      bgStyle: 'light',
      copyNotes: '',
    },
  };
};

/**
 * Remove a section by index and sync concept sections.
 * Returns updated expanded index (or null).
 */
export const calcExpandedAfterRemove = (
  removedIdx: number,
  currentExpanded: number | null,
): number | null => {
  if (currentExpanded === removedIdx) {
    return null;
  }
  if (currentExpanded !== null && currentExpanded > removedIdx) {
    return currentExpanded - 1;
  }
  return currentExpanded;
};

// ── PROGRESS & STATUS ──────────────────────────────────────────────

export interface ProgressInfo {
  /** Number of pending sections */
  pending: number;
  /** Number of completed sections */
  done: number;
  /** Total buildable sections */
  total: number;
  /** Progress percentage (0-100) */
  pct: number;
  /** All sections + shell complete */
  allDone: boolean;
  /** At least one done, none building, shell ready */
  canExport: boolean;
  /** Currently building section, if any */
  building: Section | undefined;
  /** Shared header/footer are ready (for extra pages) */
  sharedReady: boolean;
}

/**
 * Calculate build progress for a page.
 * Pure computation — no side effects.
 */
export const getProgressInfo = (page: Page, isExtraPage: boolean): ProgressInfo => {
  const buildable = isExtraPage
    ? page.sects.filter(s => s.type !== 'header' && s.type !== 'footer')
    : page.sects;

  const pending = buildable.filter(s => s.status === 'pending').length;
  const done = buildable.filter(s => s.status === 'done').length;
  const building = buildable.find(s => s.status === 'building');
  const total = buildable.length;

  const sharedReady =
    !isExtraPage ||
    page.sects
      .filter(s => s.type === 'header' || s.type === 'footer')
      .every(s => s.status === 'done');

  const allDone = total > 0 && done === total && sharedReady && page.shellStatus === 'done';
  const pct = page.shellStatus === 'done' && total > 0 ? Math.round((done / total) * 100) : 0;
  const canExport = done > 0 && !building && page.shellStatus === 'done';

  return { pending, done, total, pct, allDone, canExport, building, sharedReady };
};

/**
 * Generate human-readable status text for the build progress bar.
 */
export const getStatusText = (
  progress: ProgressInfo,
  siteName: string,
  isExtraPage: boolean,
): string => {
  if (progress.allDone) {
    return `All ${progress.total} sections complete — ${siteName}`;
  }
  if (progress.building) {
    return `Building "${progress.building.title}" (${progress.done + 1}/${progress.total})…`;
  }
  if (isExtraPage && !progress.sharedReady && progress.done === progress.total) {
    return 'Waiting for header/footer — build Home page first';
  }
  return `${progress.done} of ${progress.total} sections ready — ${siteName}`;
};

// ── SECTION TYPE FILTER ────────────────────────────────────────────

/**
 * Filter available section types for a page.
 * Extra pages can't add header/footer (shared from home).
 */
export const getAvailableTypes = (
  sectionTypes: SectionType[],
  isExtraPage: boolean,
): SectionType[] =>
  isExtraPage ? sectionTypes.filter(t => t.id !== 'header' && t.id !== 'footer') : sectionTypes;
